<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { SoldOutBanner as SoldOutIcon } from '@xpos/icons-svg';
// 新增事件声明
const emit = defineEmits(['click'])

const $slots = defineSlots();
const props = defineProps({
  debounceTime: { type: Number, default: 0 },
  // 是否为方形按钮
  square: {
    type: Boolean,
    default: false,
  },
  bg: {
    type: String,
  },
  // 是否有边框
  border: {
    type: Boolean,
    default: true,
  },
  block: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false,
  },
  // 是否售罄
  soldOut: {
    type: Boolean,
    default: false,
  },
});
// 修改 isDisabled 的定义
// const isDisabled = computed(() => props.disabled)
const isDisabled = ref(props.disabled);

// 添加 watch 来监视 props.disabled 的变化
watch(
  () => props.disabled,
  (newVal) => {
    isDisabled.value = newVal;
  }
);

const slotsLength = Object.keys($slots).length;
let justifyContent = "";

if (slotsLength === 1 && $slots.footer) {
  justifyContent = "end";
} else if ($slots.footer && $slots.header && slotsLength === 2) {
  justifyContent = "space-between";
} else if (slotsLength === 1 && $slots.default) {
  justifyContent = "center";
} else if (slotsLength === 2 && $slots.footer && $slots.default) {
  justifyContent = "space-around";
}

const styles = computed(() => {
  const styleObj: { [key: string]: string } = {}
  if (props.bg) {
    styleObj.backgroundImage = `url(${props.bg})`
    styleObj.backgroundSize = '100% 100%'
    styleObj.backgroundRepeat = 'no-repeat'
    styleObj.color = '#fff'
  }
  if (props.border) {
    styleObj.border = 'none'
  }
  return styleObj
})

// Timestamp-based debounce for synchronous blocking of rapid clicks
let lastClickTime = 0
let timeoutId: ReturnType<typeof setTimeout> | null = null
const handleClick = (event: MouseEvent) => {
  if (!props.debounceTime) {
    emit('click', event)
    return
  }
  const now = Date.now()
  if (now - lastClickTime < props.debounceTime) return // sync block
  lastClickTime = now
  isDisabled.value = true
  emit('click', event)
  if (timeoutId) clearTimeout(timeoutId)
  timeoutId = setTimeout(() => { isDisabled.value = false }, props.debounceTime)
}
onUnmounted(() => { if (timeoutId) clearTimeout(timeoutId) })
</script>
<template>
  <div class="relative">
    <el-button v-bind="$attrs" :disabled="isDisabled"
      :class="{ 'button-square': props.square, 'w-full block': props.block, 'sold-out': props.soldOut }" class="btn" :style="styles" :color="color"
      @click="handleClick">

      <div class="flex flex-col items-center h-full" :style="{ justifyContent }">
        <div v-if="$slots.header" class="btn-header">
          <slot name="header"> </slot>
        </div>
        <slot></slot>
        <div class="btn-footer" v-if="$slots.footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </el-button>

    <!-- sold-out badge -->
    <div v-if="props.soldOut"
      class="sold-out-badge absolute top-2 w-[97px] flex items-center justify-center gap-1 z-10">
      <div class="w-5 h-5 text-[#3a3a3a] flex items-center justify-center">
        <SoldOutIcon />
      </div>
      <div class="text-[#3a3a3a] text-sm font-bold leading-none whitespace-nowrap">SOLD OUT</div>
    </div>
  </div>
</template>
<style lang="scss">
.btn {
  > span {
    display: flex;
    flex-direction: column;
    padding: 4px;
    white-space: break-spaces;
    height: 100%;
    justify-content: center;
  }

  .btn-header {
    width: 100%;
  }

  &.has-footer {
    span {
      height: 100%;
      justify-content: space-between;
      padding-top: 20px;
    }
  }
}

.btn-footer {
  width: 100%;
}

/* Square button sizing */
.button-square.el-button--large {
  min-width: 108px;
  max-width: 108px;
  width: 108px !important;
  flex-shrink: 0;
}

/* Sold out styling */
.btn.sold-out {
  position: relative;
  opacity: 0.5 !important;
}

.sold-out-badge {
  left: 0.625rem !important; /* 10px */
}
</style>
